import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * Holds the current human support agent's display info — read by
 * the EscalationStatusBanner ("Connected with Casey") and by
 * AskRedoMessage when rendering escalation-human-prefixed bubbles
 * (avatar at the left of the bubble).
 *
 * Owned by `AskRedoEscalationWatcher` at the layout level so the
 * agent info SURVIVES slide-out close/reopen — the SSE subscription
 * captures it once and any later mount reads from here.
 */
export interface EscalationAuthor {
  name: string | null;
  avatarUrl: string | null;
}

interface EscalationAuthorContextValue extends EscalationAuthor {
  /** Called by the layout-level SSE handler when a new human message
   *  arrives. Merges in any newly-discovered name/avatar — null inputs
   *  are ignored so we never blank out a previously-captured value. */
  recordAuthor: (info: Partial<EscalationAuthor>) => void;
  /** Reset to null/null. Called when an escalation closes or the
   *  merchant starts a new conversation. */
  clearAuthor: () => void;
}

const noop = () => undefined;

export const EscalationAuthorContext =
  createContext<EscalationAuthorContextValue>({
    name: null,
    avatarUrl: null,
    recordAuthor: noop,
    clearAuthor: noop,
  });

export function useEscalationAuthor(): EscalationAuthorContextValue {
  return useContext(EscalationAuthorContext);
}

/**
 * State-owning provider. Mount once at the layout level. The
 * watcher pushes incoming author info via `recordAuthor`; UI
 * consumers (slide-out, page) read `name` + `avatarUrl`.
 */
export function EscalationAuthorProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [name, setName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const recordAuthor = useCallback((info: Partial<EscalationAuthor>) => {
    if (info.name != null) {
      setName((prev) => (prev === info.name ? prev : (info.name ?? null)));
    }
    if (info.avatarUrl != null) {
      setAvatarUrl((prev) =>
        prev === info.avatarUrl ? prev : (info.avatarUrl ?? null),
      );
    }
  }, []);

  const clearAuthor = useCallback(() => {
    setName(null);
    setAvatarUrl(null);
  }, []);

  const value = useMemo(
    () => ({ name, avatarUrl, recordAuthor, clearAuthor }),
    [name, avatarUrl, recordAuthor, clearAuthor],
  );

  return (
    <EscalationAuthorContext.Provider value={value}>
      {children}
    </EscalationAuthorContext.Provider>
  );
}
